<script setup lang="ts">
import * as Sentry from '@sentry/vue';
import BulkRowPlace from './BulkRowPlace.vue';
import { computed, ref, watch } from 'vue';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { isSubmitShortcut, submitShortcutLabel } from '@/utils/submitShortcut';
import type { BulkSelectionPayload } from '@/composables/useBulkSelection';
import {
  bulkApiErrorMessage,
  postBulkCancel,
  postBulkExecute,
  postBulkPreview,
  readBulkApiError,
  type BulkOperation,
  type BulkRun,
  type BulkRunItem,
} from '@/composables/api/useBulkActionApi';
import type { MixedBulkRun, MixedRunKind } from '@/composables/useMixedBulkRun';
import type { SchedulesBeforeRun } from '@/composables/api/bulkActionCachePatch';

/**
 * The dry run, and the choice to save the rest or cancel everything.
 *
 * Opening the dialog posts the preview. The server checks every row and writes nothing. The dialog
 * then says how many rows will change and lists the ones that can't, by list, zone and reason.
 *
 * - When the search now matches a different number of rows (409), the dialog shows the new count
 *   and asks again. Confirming posts the preview again with the new count.
 * - When the selection is over the cap (422 on `selection`), the dialog closes and hands the
 *   server's message to the bar, which shows it.
 * - Save executes the run. Cancel, or closing the dialog, cancels the previewed run.
 * - While Save is starting the run, the dialog cannot be closed. A close then would cancel the
 *   preview the execute is using.
 *
 * With `mixedRun`, the dialog confirms one action on lists and schedules together. The coordinator
 * (`useMixedBulkRun`) previews, executes and cancels both runs. The dialog shows its one sentence,
 * such as "Delete 3 lists (with 27 schedules) and 14 schedules", then a section per run with that
 * run's rows that can't be changed.
 */
const props = defineProps<{
  entitySlug: string;
  selection: BulkSelectionPayload | null;
  operation: BulkOperation | null;
  /** One line per change, for example "Shift End Date forward 7 days". */
  operationSummary?: string[];
  /** The coordinator of an action on lists and schedules. The bar has already asked it to preview. */
  mixedRun?: MixedBulkRun | null;
  /**
   * Reads the cached schedules for a schedule run, just before execute is posted. A live poll
   * that lands while execute is in flight may already hold shifted dates.
   */
  snapshotBeforeExecute?: (entitySlug: string) => SchedulesBeforeRun | undefined;
}>();

const open = defineModel<boolean>('open', { default: false });

const emit = defineEmits<{
  /** Execute was accepted. The run is queued. It carries the schedules read before execute. */
  (e: 'started', run: BulkRun, schedulesBeforeRun: SchedulesBeforeRun | undefined): void;
  /** The server refused the selection outright, for example over the 1,000 cap. */
  (e: 'refused', message: string): void;
  /** The action on lists and schedules was started. It names the kinds it covered. */
  (e: 'mixed-started', kinds: MixedRunKind[]): void;
}>();

type Phase = 'checking' | 'ready' | 'count_changed' | 'failed' | 'saving';

const phase = ref<Phase>('checking');
const previewRun = ref<BulkRun | null>(null);
/**
 * The entity the preview was posted to. Execute and cancel go there, even if the bar has since
 * pointed `entitySlug` at another entity.
 */
let previewEntitySlug = '';
const errorMessage = ref<string | null>(null);
/** The 409's message and new total, while the dialog asks again. */
const countChange = ref<{ message: string; currentCount: number } | null>(null);
/** The selection the next preview posts. A 409 moves its `expected_count` to the new total. */
const activeSelection = ref<BulkSelectionPayload | null>(null);
/** Set once execute is accepted, so closing the dialog does not cancel a run that is running. */
let executed = false;
/**
 * Bumped by every check and by every close. A check's answer that lands after a newer check, or
 * after the dialog closed, belongs to something the user no longer sees. It must not become the
 * run Save executes, so it is dropped and its run is cancelled.
 */
let previewCount = 0;

function cancelRun(entitySlug: string, run: BulkRun) {
  if (run.status !== 'previewed') return;
  // Nothing was written, and the preview expires on its own after 15 minutes. So a failed
  // cancel only leaves a harmless previewed row behind.
  postBulkCancel(entitySlug, run.id).catch((error) => {
    Sentry.captureException(error);
  });
}

async function runPreview() {
  if (!activeSelection.value || !props.operation) return;
  const thisPreview = ++previewCount;
  const entitySlug = props.entitySlug;
  phase.value = 'checking';
  errorMessage.value = null;
  countChange.value = null;
  previewRun.value = null;
  try {
    const run = await postBulkPreview(entitySlug, activeSelection.value, props.operation);
    if (thisPreview !== previewCount) {
      cancelRun(entitySlug, run);
      return;
    }
    previewEntitySlug = entitySlug;
    previewRun.value = run;
    phase.value = 'ready';
  } catch (error) {
    if (thisPreview !== previewCount) return;
    const { status, body } = readBulkApiError(error);
    if (status === 409 && body.code === 'count_changed' && typeof body.current_count === 'number') {
      countChange.value = {
        message: body.message ?? `The search now matches ${body.current_count.toLocaleString()} rows.`,
        currentCount: body.current_count,
      };
      phase.value = 'count_changed';
      return;
    }
    if (status === 422 && body.errors?.selection?.[0]) {
      emit('refused', body.errors.selection[0]);
      open.value = false;
      return;
    }
    errorMessage.value = bulkApiErrorMessage(error, "Couldn't check the rows. Try again.");
    phase.value = 'failed';
  }
}

/** Forget the last opening's check, so nothing of it shows in the next one. */
function resetState() {
  phase.value = 'checking';
  errorMessage.value = null;
  countChange.value = null;
}

watch(
  open,
  (isOpen) => {
    if (!isOpen) return;
    executed = false;
    resetState();
    if (props.mixedRun) return;
    activeSelection.value = props.selection ? { ...props.selection } : null;
    void runPreview();
  },
  { immediate: true },
);

/** Confirm again after a 409: the new total becomes the count the user has now seen. */
function confirmNewCount() {
  if (props.mixedRun) {
    void props.mixedRun.confirmNewCount();
    return;
  }
  const selection = activeSelection.value;
  // Both "all matching" modes carry a count: `search`, and `list_search` for schedules.
  if (!countChange.value || !selection || selection.mode === 'ids') return;
  activeSelection.value = { ...selection, expected_count: countChange.value.currentCount };
  void runPreview();
}

function cancelPreviewedRun() {
  const run = previewRun.value;
  if (!run || executed) return;
  previewRun.value = null;
  cancelRun(previewEntitySlug, run);
}

// Closing the dialog any way other than Save cancels the preview, and one still being checked.
watch(open, (isOpen) => {
  if (isOpen) return;
  previewCount++;
  cancelPreviewedRun();
  if (props.mixedRun && !executed) props.mixedRun.cancel();
  resetState();
});

// The coordinator's cap refusal goes to the bar, as the single run's does.
watch(
  () => (open.value ? (props.mixedRun?.refusal.value ?? null) : null),
  (message) => {
    if (!message) return;
    emit('refused', message);
    open.value = false;
  },
);

async function saveMixed(mixedRun: MixedBulkRun) {
  const coveredKinds = await mixedRun.execute();
  if (!coveredKinds) return;
  executed = true;
  emit('mixed-started', coveredKinds);
  open.value = false;
}

async function save() {
  if (props.mixedRun) return saveMixed(props.mixedRun);
  const run = previewRun.value;
  if (!run) return;
  phase.value = 'saving';
  errorMessage.value = null;
  const schedulesBeforeRun = props.snapshotBeforeExecute?.(previewEntitySlug);
  try {
    const executedRun = await postBulkExecute(previewEntitySlug, run.id);
    executed = true;
    emit('started', executedRun, schedulesBeforeRun);
    open.value = false;
  } catch (error) {
    const { status, body } = readBulkApiError(error);
    if (status === 410 || body.code === 'preview_expired') {
      // The preview is too old to run. Check the rows again, so the user confirms fresh counts.
      await runPreview();
      errorMessage.value = 'That check was more than 15 minutes old, so the rows were checked again. Confirm the new counts.';
      return;
    }
    errorMessage.value = bulkApiErrorMessage(error, "Couldn't start the bulk action. Try again.");
    phase.value = 'ready';
  }
}

const isDelete = computed(() => props.operation?.action === 'delete');

/** The phase the template shows. The coordinator's idle, before it is asked to preview, reads as checking. */
const shownPhase = computed<Phase>(() => {
  const mixedPhase = props.mixedRun?.phase.value;
  if (mixedPhase === undefined) return phase.value;
  return mixedPhase === 'idle' ? 'checking' : mixedPhase;
});
const shownCountChange = computed(() => (props.mixedRun ? props.mixedRun.countChange.value : countChange.value));
const isSaving = computed(() => shownPhase.value === 'saving');

/** Closing is ignored while Save is starting the run. */
function onOpenChange(isOpen: boolean) {
  if (!isOpen && isSaving.value) return;
  open.value = isOpen;
}
const shownError = computed(() => (props.mixedRun ? props.mixedRun.errorMessage.value : errorMessage.value));

const saveShortcut = submitShortcutLabel();

/**
 * Cmd+Enter presses Save once it is enabled. A delete is left to the button, so one key press
 * straight after the edit dialog's can never delete rows.
 */
function onKeydown(event: KeyboardEvent) {
  if (!isSubmitShortcut(event)) return;
  event.preventDefault();
  // canSave is false outside the ready phase, so it also covers a changed count and a failure.
  if (isDelete.value || !canSave.value) return;
  save();
}

/** One previewed run's counts and rows. Two for an action on lists and schedules, each with a heading. */
interface PreviewSection {
  key: string;
  heading: string | null;
  run: BulkRun;
}

const sections = computed<PreviewSection[]>(() => {
  if (props.mixedRun) {
    return props.mixedRun.previews.value.map((part) => ({
      key: part.kind,
      heading: part.kind === 'lists' ? 'Custom lists' : 'Schedules',
      run: part.run,
    }));
  }
  return previewRun.value ? [{ key: 'run', heading: null, run: previewRun.value }] : [];
});

function cannotChangeRowsOf(run: BulkRun): BulkRunItem[] {
  return (run.problem_rows ?? []).filter((row) => row.verdict === 'invalid' || row.verdict === 'not_found');
}

function noValueRowsOf(run: BulkRun): BulkRunItem[] {
  return (run.problem_rows ?? []).filter((row) => row.verdict === 'no_value');
}

function problemCountOf(run: BulkRun): number {
  return run.counts.invalid + run.counts.not_found + run.counts.no_value;
}

const problemCount = computed(() => sections.value.reduce((sum, section) => sum + problemCountOf(section.run), 0));

function plural(count: number, noun: string): string {
  return `${count.toLocaleString()} ${noun}${count === 1 ? '' : 's'}`;
}

function headlineOf(run: BulkRun): string {
  const counts = run.counts;
  const parts = [
    isDelete.value
      ? `${counts.will_change.toLocaleString()} will be deleted.`
      : `${counts.will_change.toLocaleString()} will change.`,
  ];
  const problems = problemCountOf(run);
  if (problems > 0) parts.push(`${problems.toLocaleString()} can't be changed.`);
  if (counts.unchanged > 0) {
    const unchanged = counts.unchanged;
    parts.push(
      unchanged === 1
        ? '1 already matches, so it is left alone.'
        : `${unchanged.toLocaleString()} already match, so they are left alone.`,
    );
  }
  return parts.join(' ');
}

const title = computed(() => {
  switch (props.operation?.action) {
    case 'delete':
      return 'Confirm the delete';
    case 'add_tag':
      return 'Confirm adding tags';
    case 'remove_tag':
      return 'Confirm removing tags';
    default:
      return 'Confirm the bulk edit';
  }
});

const saveLabel = computed(() => {
  if (problemCount.value > 0) return isDelete.value ? 'Delete the rest' : 'Save the rest';
  return isDelete.value ? 'Delete' : 'Save';
});

const canSave = computed(() => {
  if (props.mixedRun) return props.mixedRun.canExecute.value;
  return phase.value === 'ready' && (previewRun.value?.counts.will_change ?? 0) > 0;
});

function retry() {
  if (props.mixedRun) void props.mixedRun.retry();
  else void runPreview();
}

</script>

<template>
  <Dialog :open="open" @update:open="onOpenChange">
    <DialogContent class="sm:max-w-xl" data-testid="bulk-confirm-dialog" @keydown="onKeydown">
      <DialogHeader>
        <DialogTitle>{{ title }}</DialogTitle>
        <DialogDescription>Nothing has been changed yet.</DialogDescription>
      </DialogHeader>

      <ul
        v-if="operationSummary && operationSummary.length > 0"
        class="text-muted-foreground list-disc pl-5 text-sm"
        data-testid="bulk-confirm-summary"
      >
        <li v-for="line in operationSummary" :key="line">{{ line }}</li>
      </ul>

      <p v-if="shownPhase === 'checking'" class="text-muted-foreground text-sm" data-testid="bulk-confirm-checking">
        Checking every row…
      </p>

      <div v-else-if="shownPhase === 'count_changed' && shownCountChange" class="flex flex-col gap-2" data-testid="bulk-confirm-count-changed">
        <p class="text-sm">{{ shownCountChange.message }}</p>
      </div>

      <p v-else-if="shownPhase === 'failed'" class="text-destructive text-sm" data-testid="bulk-confirm-failed">
        {{ shownError }}
      </p>

      <div v-else-if="sections.length > 0" class="flex max-h-[60vh] flex-col gap-3 overflow-auto">
        <p v-if="mixedRun" class="text-sm font-medium" data-testid="bulk-confirm-mixed-headline">
          {{ mixedRun.confirmationText.value }}
        </p>

        <section
          v-for="section in sections"
          :key="section.key"
          class="flex flex-col gap-3"
          :data-testid="section.heading ? `bulk-confirm-section-${section.key}` : undefined"
        >
          <h3 v-if="section.heading" class="text-sm font-medium">{{ section.heading }}</h3>
          <p :class="section.heading ? 'text-sm' : 'text-sm font-medium'" data-testid="bulk-confirm-headline">{{ headlineOf(section.run) }}</p>

          <p
            v-if="isDelete && section.run.delete_impact"
            class="text-sm"
            data-testid="bulk-confirm-delete-impact"
          >
            {{ plural(section.run.delete_impact.count, 'schedule') }}
            {{ section.run.delete_impact.count === 1 ? 'goes' : 'go' }} with these lists and
            {{ section.run.delete_impact.count === 1 ? 'is' : 'are' }} deleted too. Deletes are permanent.
          </p>
          <p v-else-if="isDelete" class="text-sm">Deletes are permanent.</p>

          <p
            v-if="section.run.counts.skipped_for_access > 0"
            class="text-muted-foreground text-sm"
            data-testid="bulk-confirm-skipped-for-access"
          >
            {{ plural(section.run.counts.skipped_for_access, 'row') }} you don't have access to
            {{ section.run.counts.skipped_for_access === 1 ? 'was' : 'were' }} skipped.
          </p>

          <section v-if="cannotChangeRowsOf(section.run).length > 0" data-testid="bulk-confirm-cannot-change">
            <h3 class="mb-1 text-sm font-medium">Can't be changed</h3>
            <ul class="divide-border/60 divide-y rounded-md border text-sm">
              <li
                v-for="row in cannotChangeRowsOf(section.run)"
                :key="row.id"
                class="flex flex-col px-2 py-1.5"
                :data-testid="`bulk-confirm-problem-${row.record_id}`"
              >
                <BulkRowPlace :item="row" />
                <span class="text-muted-foreground">{{ row.message ?? row.reason }}</span>
              </li>
            </ul>
          </section>

          <section v-if="noValueRowsOf(section.run).length > 0" data-testid="bulk-confirm-no-value">
            <h3 class="mb-1 text-sm font-medium">No value</h3>
            <ul class="divide-border/60 divide-y rounded-md border text-sm">
              <li
                v-for="row in noValueRowsOf(section.run)"
                :key="row.id"
                class="flex flex-col px-2 py-1.5"
                :data-testid="`bulk-confirm-no-value-${row.record_id}`"
              >
                <BulkRowPlace :item="row" />
                <span class="text-muted-foreground">{{ row.message ?? 'The field is empty, so there was nothing to change.' }}</span>
              </li>
            </ul>
          </section>

          <p v-if="section.run.counts.will_change === 0" class="text-muted-foreground text-sm">Nothing will change.</p>
        </section>
      </div>

      <p v-if="shownError && shownPhase !== 'failed'" class="text-destructive text-sm" data-testid="bulk-confirm-error">
        {{ shownError }}
      </p>

      <DialogFooter>
        <Button variant="outline" data-testid="bulk-confirm-cancel" :disabled="isSaving" @click="open = false">Cancel</Button>
        <Button
          v-if="shownPhase === 'count_changed' && shownCountChange"
          data-testid="bulk-confirm-recheck"
          @click="confirmNewCount"
        >
          Check {{ shownCountChange.currentCount.toLocaleString() }} rows
        </Button>
        <Button v-else-if="shownPhase === 'failed'" data-testid="bulk-confirm-retry" @click="retry">Try again</Button>
        <Button
          v-else
          :variant="isDelete ? 'destructive' : 'default'"
          :disabled="!canSave"
          :title="isDelete ? undefined : `${saveLabel} (${saveShortcut})`"
          data-testid="bulk-confirm-save"
          @click="save"
        >
          {{ shownPhase === 'saving' ? 'Starting…' : saveLabel }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
